import React, { useMemo, useState } from "react";
import { Button, Upload, Tag, Icon, Typography } from "antd";
import { RcFile } from "antd/lib/upload";

import { validateFile } from "../../helper/FileValidator";
import ApiService from "../../services/ApiService";

import { FileStatus } from "../formHelpers/FormFileUpload";
import { ServiceType } from "../../services/ServiceType.data";
import { QueryPath } from "../../services/QueryPath.data";
import { FileConfig, DEFAULT_FILE_CONFIG } from "../../Constants";

const { Text } = Typography;

interface SingleFileUploadProps {
  label: string;
  disabled?: boolean;
  className?: string;
  uploadClassName?: string;
  acceptableFileExtensions?: string[];
  maximumFileSize?: number;
  onError?: (errorMsg: string) => void;
  onSuccess?: (fileName: string) => void;
  config: {
    uploadServiceType: ServiceType;
    uploadPath: QueryPath;
    /**
     * use the uploadServiceType if deleteServiceType is not defined explicitly
     */
    deleteServiceType?: ServiceType;
    /**
     * use the uploadPath if deletePath is not defined explicitly
     */
    deletePath?: QueryPath;
    uploadParams?: object;
  };
}

// Shape of local state. Differs from FormFileUpload's FileObject in not having 'removed' field.
interface SingleFileObject {
  id: string;
  name: string;
  status: FileStatus | null;
  error: string;
}

const initialState = {
  id: "",
  name: "",
  status: null,
  error: ""
};

/**
 * A Single file upload component to be used directly without needing Formik.
 */
const SingleFileUpload: React.FC<SingleFileUploadProps> = (
  props: SingleFileUploadProps
) => {
  const {
    label,
    className,
    uploadClassName,
    disabled,
    acceptableFileExtensions,
    maximumFileSize,
    onError,
    onSuccess,
    config
  } = props;
  const [file, setFile] = useState<SingleFileObject>(initialState);
  const { error, name, status, id } = file;

  const accept: string = useMemo(
    () =>
      acceptableFileExtensions ? acceptableFileExtensions.join(",") : ".pdf",
    [acceptableFileExtensions]
  );

  const fileConfig: FileConfig = useMemo(
    () => ({
      ...DEFAULT_FILE_CONFIG,
      acceptableFileExtensions:
        acceptableFileExtensions ||
        DEFAULT_FILE_CONFIG.acceptableFileExtensions,
      maximumFileSize: maximumFileSize || DEFAULT_FILE_CONFIG.maximumFileSize
    }),
    [acceptableFileExtensions, maximumFileSize]
  );

  const handleAdd = async (fileObj: RcFile) => {
    const fileInvalid = validateFile(fileObj, fileConfig);
    try {
      setFile({
        name: fileObj.name,
        status: fileInvalid ? FileStatus.Invalid : FileStatus.Processing,
        id: fileObj.uid,
        error: fileInvalid
      });
      if (fileInvalid && onError) {
        onError(fileInvalid);
      } else {
        const uploadStatus = await upload(fileObj);
        if (uploadStatus) {
          setFile(prevFile => ({
            ...prevFile,
            status: FileStatus.Success,
            id: uploadStatus.id
          }));
        }
        setFile(prevFile => ({ ...prevFile, error: "" }));
        if (onSuccess) {
          onSuccess(file.name);
        }
      }
    } catch (error) {
      const errorMessage = error.message || JSON.stringify(error);
      setFile(prevFile => ({
        ...prevFile,
        error: errorMessage,
        status: FileStatus.Failed
      }));
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  const upload = (fileObj: RcFile): Promise<any> => {
    return new ApiService(config.uploadServiceType, true).postMultipart(
      config.uploadPath,
      {
        file: fileObj,
        ...config.uploadParams
      }
    );
  };

  const remove = (): boolean | Promise<boolean> => {
    return new ApiService(
      config.deleteServiceType
        ? config.deleteServiceType
        : config.uploadServiceType
    ).delete({
      route: config.deletePath
        ? config.deletePath.route
        : config.uploadPath.route,
      query: {
        docId: id
      }
    });
  };

  const handleClose = async () => {
    if (status === FileStatus.Processing) {
      return;
    }
    try {
      if (status === FileStatus.Success) {
        await remove();
      }
      setFile(initialState);
    } catch (error) {
      const errorMessage = error.message || JSON.stringify(error);
      setFile(prevState => ({ ...prevState, error: errorMessage }));
      if (onError) {
        onError(errorMessage);
      }
    }
  };

  return (
    <div className={`${className}`}>
      <Upload
        className={uploadClassName}
        disabled={disabled}
        accept={accept}
        beforeUpload={(fileObj: RcFile) => {
          handleAdd(fileObj);
          return false;
        }}
        showUploadList={false}
      >
        <Button type="link" disabled={disabled} className="p-0">
          {label}
        </Button>
      </Upload>
      {error && <Text className="error d-block">{error}</Text>}
      {name && (
        <Tag
          closable={status !== FileStatus.Processing && !disabled}
          onClose={handleClose}
          className={`px-2 mr-1 mb-1 d-flex align-items-center ant-tag-ellipse ${
            status === FileStatus.Success
              ? "ant-tag-green"
              : status === FileStatus.Processing
              ? "ant-tag-grey"
              : "ant-tag-red"
          }`}
        >
          <Text className="ant-tag-name-ellipsis">{name}</Text>
          {status === FileStatus.Processing && <Icon type="loading" />}
        </Tag>
      )}
    </div>
  );
};

export default SingleFileUpload;
